/**
 * Shared colour utilities — single source of truth for status/score/rank colours.
 * All class strings include both light and dark variants.
 */

// ── Score colours (SEO score, PageSpeed, readability) ──────────────

export function scoreColor(score: number, thresholds = { good: 80, ok: 50 }): string {
  if (score >= thresholds.good) return 'text-green-600 dark:text-green-400'
  if (score >= thresholds.ok) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

export function scoreBgColor(score: number, thresholds = { good: 80, ok: 50 }): string {
  if (score >= thresholds.good) return 'bg-green-500/80'
  if (score >= thresholds.ok) return 'bg-yellow-500/80'
  return 'bg-red-500/80'
}

/** Raw hex for conic-gradient / SVG (SeoScoreBadge, inline styles) */
export function scoreHex(score: number, thresholds = { good: 80, ok: 50 }): string {
  if (score >= thresholds.good) return '#22c55e'
  if (score >= thresholds.ok) return '#eab308'
  return '#ef4444'
}

// ── Rank position colours ──────────────────────────────────────────

export function rankColor(position: number): string {
  if (position <= 3) return 'text-green-600 dark:text-green-400'
  if (position <= 10) return 'text-blue-600 dark:text-blue-400'
  if (position <= 20) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-muted-foreground'
}

export function rankBadgeColor(position: number): string {
  if (position <= 3) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  if (position <= 10) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
  if (position <= 20) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
  return 'bg-muted text-muted-foreground'
}

// ── Spam score colours ─────────────────────────────────────────────

export function spamScoreColor(score: number): string {
  if (score >= 60) return 'text-red-600 dark:text-red-400'
  if (score >= 30) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-green-600 dark:text-green-400'
}

export function spamScoreBadgeColor(score: number): string {
  if (score >= 60) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  if (score >= 30) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
  return 'bg-muted text-muted-foreground'
}

// ── Status badge presets ───────────────────────────────────────────

export const STATUS_BADGE = {
  success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  neutral: 'bg-muted text-muted-foreground',
} as const

// ── Priority colours ───────────────────────────────────────────────

export const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
  medium: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
  low: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
}

// ── Change indicator colours ───────────────────────────────────────

export function changeColor(value: number): string {
  if (value > 0) return 'text-green-600 dark:text-green-400'
  if (value < 0) return 'text-red-600 dark:text-red-400'
  return 'text-muted-foreground'
}

// ── Chart palette (CSS variable references for Recharts SVG) ───────

export const CHART_PALETTE = [
  'var(--chart-primary)',
  'var(--chart-success)',
  'var(--chart-warning)',
  'var(--chart-error)',
  'var(--chart-purple)',
  'var(--chart-7)',
  'var(--chart-8)',
  'var(--chart-9)',
  'var(--chart-10)',
  'var(--chart-4)',
]
